import * as Icons from '@ant-design/icons';
import React from 'react';

/**
 * 强制图标修复工具
 * 直接创建可用的图标组件，绕过转换过程
 */

// 创建预定义的图标组件映射
const FORCED_ICON_COMPONENTS: Record<string, React.ReactNode> = {
  // 组织管理相关
  ApartmentOutlined: React.createElement(Icons.ApartmentOutlined),
  UserOutlined: React.createElement(Icons.UserOutlined),
  TeamOutlined: React.createElement(Icons.TeamOutlined),
  IdcardOutlined: React.createElement(Icons.IdcardOutlined),

  // 系统管理相关
  SettingOutlined: React.createElement(Icons.SettingOutlined),
  MenuOutlined: React.createElement(Icons.MenuOutlined),
  SafetyCertificateOutlined: React.createElement(
    Icons.SafetyCertificateOutlined,
  ),
  BookOutlined: React.createElement(Icons.BookOutlined),

  // 设备管理相关
  DesktopOutlined: React.createElement(Icons.DesktopOutlined),
  DatabaseOutlined: React.createElement(Icons.DatabaseOutlined),
  CloudServerOutlined: React.createElement(Icons.CloudServerOutlined),
  GatewayOutlined: React.createElement(Icons.GatewayOutlined),

  // 事件管理相关
  AlertOutlined: React.createElement(Icons.AlertOutlined),
  HistoryOutlined: React.createElement(Icons.HistoryOutlined),
  FileTextOutlined: React.createElement(Icons.FileTextOutlined),
  NodeIndexOutlined: React.createElement(Icons.NodeIndexOutlined),
  BellOutlined: React.createElement(Icons.BellOutlined),
  FileOutlined: React.createElement(Icons.FileOutlined),

  // 套餐管理相关
  GiftOutlined: React.createElement(Icons.GiftOutlined),
  TagsOutlined: React.createElement(Icons.TagsOutlined),

  // 基础图标
  HomeOutlined: React.createElement(Icons.HomeOutlined),
  SmileOutlined: React.createElement(Icons.SmileOutlined),
  BugOutlined: React.createElement(Icons.BugOutlined),
  DashboardOutlined: React.createElement(Icons.DashboardOutlined),

  // 视频融合相关
  PlayCircleOutlined: React.createElement(Icons.PlayCircleOutlined),
  VideoCameraOutlined: React.createElement(Icons.VideoCameraOutlined),
  CameraOutlined: React.createElement(Icons.CameraOutlined),

  // 补充缺失的图标
  TranslationOutlined: React.createElement(Icons.TranslationOutlined),
  TableOutlined: React.createElement(Icons.TableOutlined),
};

/**
 * 强制获取图标组件 - 绕过所有转换逻辑
 * @param iconName 图标名称
 * @returns React图标组件或null
 */
export function forceGetIconComponent(
  iconName: string | undefined,
): React.ReactNode {
  if (!iconName || typeof iconName !== 'string') return null;

  // 清理输入
  const cleanName = iconName.replace(/['"]/g, '').trim();

  // 直接从预定义映射中获取
  if (FORCED_ICON_COMPONENTS[cleanName]) {
    // 强制图标获取成功
    return FORCED_ICON_COMPONENTS[cleanName];
  }

  // 尝试常见的变体
  const variants = [
    cleanName,
    `${cleanName}Outlined`,
    cleanName.replace(/Outlined$/, ''),
    cleanName.toLowerCase(),
    cleanName.charAt(0).toUpperCase() + cleanName.slice(1),
  ];

  for (const variant of variants) {
    if (FORCED_ICON_COMPONENTS[variant]) {
      // 强制图标变体获取成功
      return FORCED_ICON_COMPONENTS[variant];
    }
  }

  console.warn(`❌ 强制图标获取失败: ${cleanName}`);
  // 可用的强制图标
  return null;
}

/**
 * 强制菜单图标修复
 * @param menus 菜单数据
 * @returns 修复后的菜单数据
 */
export function forceFixMenuIcons(menus: any[]) {
  if (!menus || !Array.isArray(menus)) return [];

  return menus.map((menu) => {
    // 强制获取图标组件
    const iconComponent = forceGetIconComponent(menu.icon);

    return {
      ...menu,
      icon: iconComponent || menu.icon, // 如果修复失败，保留原始值
    };
  });
}

/**
 * 验证图标组件是否存在
 * @param iconName 图标名称
 * @returns boolean
 */
export function validateIconExists(iconName: string): boolean {
  if (!iconName || typeof iconName !== 'string') return false;
  const cleanName = iconName.replace(/['"]/g, '').trim();
  return cleanName in Icons;
}

/**
 * 获取所有可用的Outlined图标
 * @returns string[]
 */
export function getAllOutlinedIcons(): string[] {
  return Object.keys(Icons).filter((key) => key.endsWith('Outlined'));
}

// 导出工具函数供调试使用
export const iconDebugTools = {
  forceGetIconComponent,
  forceFixMenuIcons,
  validateIconExists,
  getAllOutlinedIcons,
  availableForcedIcons: Object.keys(FORCED_ICON_COMPONENTS),
};
